import maplibregl from 'maplibre-gl'
import {
  ensureAnchors,
  addOverlayLayer as addOverlayLayerOrdered,
  ANCHOR_OVERLAYS,
} from './layer-order-manager.js'

export type LngLat = { lng: number; lat: number }
export type LngLatBounds = { sw: LngLat; ne: LngLat }

/** Type guard for GeoJSON sources — works with both real instances and test mocks */
export function isGeoJSONSource(
  source: maplibregl.Source | undefined
): source is maplibregl.GeoJSONSource {
  return source != null && 'setData' in source
}

export class MapEngine {
  readonly gl: maplibregl.Map
  private markers: Map<string, maplibregl.Marker> = new Map()
  private sourceCounter = 0

  constructor(
    container: HTMLElement,
    options: {
      center: [number, number]
      zoom: number
      style?: maplibregl.StyleSpecification
    }
  ) {
    const defaultStyle: maplibregl.StyleSpecification = {
      version: 8,
      sources: {
        osm: {
          type: 'raster',
          tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
          tileSize: 256,
          attribution:
            '&copy; <a href="https://www.openstreetmap.org">OpenStreetMap</a> contributors',
          maxzoom: 19,
        },
      },
      layers: [
        {
          id: 'osm-layer',
          type: 'raster',
          source: 'osm',
        },
      ],
    }

    this.gl = new maplibregl.Map({
      container,
      style: options.style ?? defaultStyle,
      center: options.center,
      zoom: options.zoom,
      attributionControl: false,
      boxZoom: false,
    })
  }

  nextId(prefix: string): string {
    return `${prefix}-${++this.sourceCounter}`
  }

  addGeoJSONSource(id: string, data: GeoJSON.GeoJSON): void {
    this.gl.addSource(id, { type: 'geojson', data })
  }

  updateGeoJSONSource(id: string, data: GeoJSON.GeoJSON): void {
    const source = this.gl.getSource(id)
    if (isGeoJSONSource(source)) {
      source.setData(data)
    }
  }

  removeSource(id: string): void {
    if (this.gl.getSource(id)) {
      this.gl.removeSource(id)
    }
  }

  addLayer(layer: maplibregl.LayerSpecification, before?: string): void {
    this.gl.addLayer(layer, before)
  }

  removeLayer(id: string): void {
    if (this.gl.getLayer(id)) {
      this.gl.removeLayer(id)
    }
  }

  setBaseLayer(
    id: string,
    url: string,
    options?: { maxzoom?: number; tileSize?: number; attribution?: string }
  ): void {
    const style = this.gl.getStyle()
    for (const layer of style.layers) {
      if (layer.id.startsWith('base-')) {
        this.gl.removeLayer(layer.id)
      }
    }
    for (const srcId of Object.keys(style.sources)) {
      if (srcId.startsWith('base-')) {
        this.gl.removeSource(srcId)
      }
    }

    const sourceId = `base-${id}`
    this.gl.addSource(sourceId, {
      type: 'raster',
      tiles: [url],
      tileSize: options?.tileSize ?? 256,
      attribution: options?.attribution ?? '',
      maxzoom: options?.maxzoom ?? 19,
    })

    // Insert below the overlays anchor so the base sits beneath every band.
    this.gl.addLayer(
      {
        id: `base-${id}-layer`,
        type: 'raster',
        source: sourceId,
      },
      this.gl.getLayer(ANCHOR_OVERLAYS) ? ANCHOR_OVERLAYS : undefined
    )
    ensureAnchors(this.gl)
  }

  addOverlaySource(
    id: string,
    url: string,
    options?: { maxzoom?: number; tileSize?: number; opacity?: number; attribution?: string }
  ): void {
    const sourceId = `overlay-${id}`
    this.gl.addSource(sourceId, {
      type: 'raster',
      tiles: [url],
      tileSize: options?.tileSize ?? 256,
      attribution: options?.attribution ?? '',
      maxzoom: options?.maxzoom ?? 19,
    })
    addOverlayLayerOrdered(this.gl, {
      id: `overlay-${id}-layer`,
      type: 'raster',
      source: sourceId,
      paint: {
        'raster-opacity': options?.opacity ?? 1,
      },
    })
  }

  removeOverlaySource(id: string): void {
    const layerId = `overlay-${id}-layer`
    const sourceId = `overlay-${id}`
    this.removeLayer(layerId)
    this.removeSource(sourceId)
  }

  addMarker(id: string, element: HTMLElement, lngLat: [number, number]): maplibregl.Marker {
    this.removeMarker(id)
    const marker = new maplibregl.Marker({ element }).setLngLat(lngLat).addTo(this.gl)
    this.markers.set(id, marker)
    return marker
  }

  updateMarkerPosition(id: string, lngLat: [number, number]): void {
    const marker = this.markers.get(id)
    if (marker) {
      marker.setLngLat(lngLat)
    }
  }

  removeMarker(id: string): void {
    const marker = this.markers.get(id)
    if (marker) {
      marker.remove()
      this.markers.delete(id)
    }
  }

  getMarker(id: string): maplibregl.Marker | undefined {
    return this.markers.get(id)
  }

  getCenter(): LngLat {
    const c = this.gl.getCenter()
    return { lng: c.lng, lat: c.lat }
  }

  getZoom(): number {
    return this.gl.getZoom()
  }

  setView(center: [number, number], zoom: number): void {
    this.gl.jumpTo({ center, zoom })
  }

  flyTo(center: [number, number], zoom?: number): void {
    this.gl.flyTo({ center, ...(zoom !== undefined && { zoom }) })
  }

  zoomIn(): void {
    this.gl.zoomIn()
  }

  zoomOut(): void {
    this.gl.zoomOut()
  }

  resize(): void {
    this.gl.resize()
  }

  enableDragging(): void {
    this.gl.dragPan.enable()
  }

  disableDragging(): void {
    this.gl.dragPan.disable()
  }

  containerPointToLngLat(point: { x: number; y: number }): LngLat {
    const ll = this.gl.unproject([point.x, point.y])
    return { lng: ll.lng, lat: ll.lat }
  }

  lngLatToContainerPoint(lngLat: LngLat): { x: number; y: number } {
    const p = this.gl.project([lngLat.lng, lngLat.lat])
    return { x: p.x, y: p.y }
  }

  on(type: string, listener: (e: unknown) => void): void {
    this.gl.on(type as keyof maplibregl.MapEventType, listener as (e: unknown) => unknown)
  }

  off(type: string, listener: (e: unknown) => void): void {
    this.gl.off(type as keyof maplibregl.MapEventType, listener as (e: unknown) => unknown)
  }

  static distance(from: LngLat, to: LngLat): number {
    const R = 6371000
    const dLat = ((to.lat - from.lat) * Math.PI) / 180
    const dLng = ((to.lng - from.lng) * Math.PI) / 180
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((from.lat * Math.PI) / 180) *
        Math.cos((to.lat * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2)
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  }

  remove(): void {
    this.markers.forEach(m => m.remove())
    this.markers.clear()
    this.gl.remove()
  }

  getContainer(): HTMLElement {
    return this.gl.getContainer()
  }
}
